/**
 * Smooth disclosure list.
 *
 * Wraps a set of <details> elements and animates their height on open and
 * close. The native element already gives us the semantics, keyboard handling
 * and a working no-JS fallback, but it snaps open. We intercept the summary
 * click, drive the height ourselves, and only flip the `open` attribute once a
 * closing animation has finished, so the content stays in the flow while it
 * collapses.
 *
 * Markup contract:
 *   <sp-accordion single duration="320">
 *     <details>
 *       <summary>…</summary>
 *       <div data-accordion-panel><div>…body…</div></div>
 *     </details>
 *   </sp-accordion>
 *
 * The panel is the animated box, so all padding belongs on the element inside
 * it. Otherwise the panel never collapses past that padding.
 */

const EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

class SpAccordion extends HTMLElement {
  connectedCallback() {
    this.single = this.hasAttribute('single');
    this.duration = Number(this.getAttribute('duration')) || 320;
    this.items = Array.from(this.querySelectorAll('details'));

    this.items.forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (event) => {
        // Enter and Space on a summary both arrive here as a click, so this
        // covers pointer and keyboard in one place.
        event.preventDefault();
        if (details.open) {
          this.collapse(details);
        } else {
          this.expand(details);
        }
      });
    });
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  panelOf(details) {
    return details.querySelector('[data-accordion-panel]');
  }

  expand(details) {
    if (this.single) {
      this.items.forEach((other) => {
        if (other !== details && other.open) this.collapse(other);
      });
    }

    const panel = this.panelOf(details);
    details.open = true;
    details.dataset.state = 'open';

    if (!panel || this.reducedMotion) return;
    this.animate(panel, 0, panel.scrollHeight);
  }

  collapse(details) {
    const panel = this.panelOf(details);
    details.dataset.state = 'closed';

    if (!panel || this.reducedMotion) {
      details.open = false;
      return;
    }

    this.animate(panel, panel.scrollHeight, 0, () => {
      // Only actually close if nothing re-opened it while we were animating.
      if (details.dataset.state === 'closed') details.open = false;
    });
  }

  animate(panel, from, to, done) {
    // A token lets a new animation invalidate the one it interrupts, so a fast
    // double click cannot leave a stale handler that closes an open panel.
    const token = (panel.spToken || 0) + 1;
    panel.spToken = token;

    if (panel.spCleanup) panel.spCleanup();

    panel.style.transition = 'none';
    panel.style.overflow = 'hidden';
    panel.style.height = `${from}px`;
    panel.getBoundingClientRect();

    panel.style.transition = `height ${this.duration}ms ${EASING}`;
    panel.style.height = `${to}px`;

    const settle = () => {
      if (panel.spToken !== token) return;
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      panel.spCleanup = null;
      panel.style.transition = '';
      panel.style.height = '';
      panel.style.overflow = '';
      if (done) done();
    };

    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      settle();
    };

    // Guard against a transitionend that never fires, for instance when the
    // two heights are identical.
    const timer = setTimeout(settle, this.duration + 60);

    panel.spCleanup = () => {
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      panel.spCleanup = null;
    };

    panel.addEventListener('transitionend', onEnd);
  }
}

if (!customElements.get('sp-accordion')) {
  customElements.define('sp-accordion', SpAccordion);
}
